import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AZURE } from '../services/api';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import { useAssistFormBridge } from '../contexts/AssistFormBridge';
import { buildUserProfileFormContext, applyUserProfilePatch } from '../components/assist/formContexts';

const LABELS = {
  pt: {
    settings: 'Configurações',
    navProfile: 'Meu Perfil',
    navNotifications: 'Notificações',
    navSecurity: 'Segurança',
    navPrivacy: 'Dados e Privacidade',
    memberSince: (d) => `Membro desde ${d}`,
    editProfile: 'Editar Perfil',
    cancel: 'Cancelar',
    saveChanges: 'Salvar Alterações',
    insertData: 'Inserir Dados',
    saving: 'Salvando...',
    personalInfo: 'Informações Pessoais',
    locationSection: 'Localização',
    sportsPrefs: 'Preferências de Esporte',
    fullName: 'Nome Completo',
    email: 'Email',
    phone: 'Telefone',
    birthDate: 'Data de Nascimento',
    gender: 'Gênero',
    state: 'Estado',
    city: 'Cidade',
    favSports: 'Esportes Favoritos',
    practiceLevel: 'Nível de Prática',
    notInformed: 'Não informado',
    noSport: 'Nenhum esporte selecionado',
    profileIncomplete: 'Complete seu perfil',
    profileIncompleteDesc: 'Preencha seus dados para aproveitar todos os recursos da plataforma.',
    profileComplete: 'Perfil atualizado',
    changePhoto: 'Alterar foto',
    removePhoto: 'Remover foto',
    photoSizeError: 'A imagem deve ter no máximo 5MB',
    photoTypeError: 'Por favor, selecione uma imagem válida',
    matchAlerts: 'Alertas de Partidas',
    newsUpdates: 'Notícias e Atualizações',
    changePassword: 'Alterar Senha',
    accountPassword: 'Senha da conta',
    savePassword: 'Salvar Nova Senha',
    savingPw: 'Alterando...',
    exportData: 'Exportar Seus Dados',
    manageAccount: 'Gerenciar Conta',
    dangerZone: 'Zona de Perigo',
    deleteAccount: 'Excluir Minha Conta',
    activeSessions: 'Sessões Ativas',
    selectGender: 'Selecione o gênero',
    selectState: 'Selecione o estado',
    selectLevel: 'Selecione o nível',
    genderM: 'Masculino',
    genderF: 'Feminino',
    genderO: 'Outro',
    genderNA: 'Prefiro não dizer',
    levelAmateur: 'Amador',
    levelSemiPro: 'Semiprofissional',
    levelProfessional: 'Profissional',
    levelCoach: 'Treinador / Comissão técnica',
    levelOther: 'Outro',
  },
  en: {
    settings: 'Settings',
    navProfile: 'My Profile',
    navNotifications: 'Notifications',
    navSecurity: 'Security',
    navPrivacy: 'Data & Privacy',
    memberSince: (d) => `Member since ${d}`,
    editProfile: 'Edit Profile',
    cancel: 'Cancel',
    saveChanges: 'Save Changes',
    insertData: 'Save Data',
    saving: 'Saving...',
    personalInfo: 'Personal Information',
    locationSection: 'Location',
    sportsPrefs: 'Sports Preferences',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    birthDate: 'Date of Birth',
    gender: 'Gender',
    state: 'State',
    city: 'City',
    favSports: 'Favorite Sports',
    practiceLevel: 'Practice Level',
    notInformed: 'Not provided',
    noSport: 'No sport selected',
    profileIncomplete: 'Complete your profile',
    profileIncompleteDesc: 'Fill in your details to unlock all platform features.',
    profileComplete: 'Profile updated',
    changePhoto: 'Change photo',
    removePhoto: 'Remove photo',
    photoSizeError: 'Image must be at most 5MB',
    photoTypeError: 'Please select a valid image',
    matchAlerts: 'Match Alerts',
    newsUpdates: 'News & Updates',
    changePassword: 'Change Password',
    accountPassword: 'Account password',
    savePassword: 'Save New Password',
    savingPw: 'Saving...',
    exportData: 'Export Your Data',
    manageAccount: 'Manage Account',
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete My Account',
    activeSessions: 'Active Sessions',
    selectGender: 'Select gender',
    selectState: 'Select state',
    selectLevel: 'Select level',
    genderM: 'Male',
    genderF: 'Female',
    genderO: 'Other',
    genderNA: 'Prefer not to say',
    levelAmateur: 'Amateur',
    levelSemiPro: 'Semi-professional',
    levelProfessional: 'Professional',
    levelCoach: 'Coach / Technical staff',
    levelOther: 'Other',
  },
};


// --- Cores da marca ---

// --- Componente de Card ---
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border shadow-sm p-6 ${className}`} style={{ borderColor: BRAND_GREEN }}>
    {children}
  </div>
);

// --- Componente de Avatar ---
const Avatar = ({ src, name, size = 80 }) => {
  const initials = name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  
  return (
    <div 
      className="rounded-full flex items-center justify-center text-white font-bold"
      style={{ 
        width: size, 
        height: size, 
        backgroundColor: BRAND_GREEN,
        fontSize: size * 0.4
      }}
    >
      {src ? (
        <img 
          src={src} 
          alt={name} 
          className="w-full h-full rounded-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div className="w-full h-full rounded-full flex items-center justify-center" style={{ display: src ? 'none' : 'flex' }}>
        {initials}
      </div>
    </div>
  );
};

// --- Componente de Toggle Switch ---
const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
    }`}
    style={{
      backgroundColor: checked ? BRAND_GREEN : '#d1d5db',
      focusRingColor: BRAND_GREEN
    }}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

// --- Componente de Sidebar ---
const ProfileSidebar = ({ activeSection, onSectionChange }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const sections = [
    { id: 'profile', label: t.navProfile, icon: '👤' },
    { id: 'notifications', label: t.navNotifications, icon: '🔔' },
    { id: 'security', label: t.navSecurity, icon: '🔒' },
    { id: 'privacy', label: t.navPrivacy, icon: '🛡️' },
  ];

  return (
    <div className="w-64 bg-white rounded-xl border shadow-sm p-4 h-fit sticky top-4">
      <h2 className="text-lg font-bold mb-6" style={{ color: BRAND_GREEN }}>
        {t.settings}
      </h2>
      <nav className="space-y-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onSectionChange(section.id)}
            className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
              activeSection === section.id
                ? 'text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            style={{
              backgroundColor: activeSection === section.id ? BRAND_GREEN : 'transparent'
            }}
          >
            <span className="text-lg">{section.icon}</span>
            <span className="font-medium">{section.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

// --- Seção 1: Meu Perfil ---
const ProfileSection = ({ userData, onUpdate }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [formData, setFormData] = useState(userData);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(userData.avatar);

  // Detecta se perfil está incompleto para exibir banner
  const isProfileIncomplete = !formData.fullName || !formData.dateOfBirth || !formData.gender || !formData.city;

  // COMPLETE_PROFILE (26/08): registra o form na AssistFormBridge — o VUKZ
  // Assist vê current_values + missing_required_fields e preenche via
  // fill_form validado (applyUserProfilePatch). Save continua sendo o
  // botão normal (onUpdate) — Aplicar NUNCA salva.
  const { registerAssistForm, unregisterAssistForm } = useAssistFormBridge();
  const formRef = useRef(formData);
  formRef.current = formData;
  const handleAssistFill = useCallback((patch, fillForm) => {
    if (fillForm?.target && fillForm.target !== 'user_profile') {
      return { applied: [], rejected: [{ field: '*', reason: `sugestão destinada a outro formulário (${fillForm.target})` }] };
    }
    const result = applyUserProfilePatch(formRef.current, patch);
    if (result.applied.length > 0) setFormData(result.nextForm);
    return { applied: result.applied, rejected: result.rejected };
  }, []);
  useEffect(() => {
    registerAssistForm({
      formId: 'user_profile',
      label: 'Meu Perfil',
      schemaVersion: 'v1',
      getFormContext: () => buildUserProfileFormContext(formRef.current),
      applyPatch: handleAssistFill,
    });
    return () => unregisterAssistForm('user_profile');
  }, [registerAssistForm, unregisterAssistForm, handleAssistFill]);

  useEffect(() => {
    setFormData(userData);
    setAvatarPreview(userData.avatar);
  }, [userData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName?.trim()) newErrors.fullName = 'Nome completo é obrigatório';
    if (!formData.email?.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      await onUpdate(formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, avatar: t.photoTypeError }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, avatar: t.photoSizeError }));
      return;
    }

    setUploadingAvatar(true);
    setErrors(prev => ({ ...prev, avatar: null }));

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      setAvatarPreview(base64String);
      try {
        const uploadResponse = await AZURE.post('/upload/avatar', { avatar: base64String });
        if (uploadResponse.data.success) {
          handleChange('avatar', base64String);
          onUpdate({ ...formData, avatar: base64String });
        }
      } catch (uploadError) {
        setErrors(prev => ({ ...prev, avatar: 'Erro ao enviar foto. Tente novamente.' }));
        setAvatarPreview(userData.avatar);
      } finally {
        setUploadingAvatar(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const inputCls = (hasError) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-gray-800 placeholder-gray-400 ${
      hasError ? 'border-red-400' : ''
    }`;

  const inputStyle = (hasError) => ({
    borderColor: hasError ? '#ef4444' : BRAND_GREEN,
  });

  return (
    <div className="space-y-6">

      {/* Banner: perfil incompleto */}
      {isProfileIncomplete && (
        <div
          className="flex items-start gap-3 px-5 py-4 rounded-xl border"
          style={{ backgroundColor: '#fffbeb', borderColor: BRAND_GOLD }}
        >
          <span className="text-2xl mt-0.5">📋</span>
          <div>
            <p className="font-semibold" style={{ color: BRAND_GOLD }}>{t.profileIncomplete}</p>
            <p className="text-sm text-gray-600 mt-0.5">{t.profileIncompleteDesc}</p>
          </div>
        </div>
      )}

      {/* Avatar + header */}
      <Card>
        <div className="flex items-center gap-5">
          <div className="relative flex-shrink-0">
            <Avatar src={avatarPreview} name={formData.fullName || 'U'} size={80} />
            <label
              htmlFor="avatar-upload"
              className="absolute -bottom-2 -right-2 cursor-pointer bg-white rounded-full p-1.5 shadow-md border-2 hover:bg-gray-50 transition-colors"
              style={{ borderColor: BRAND_GREEN }}
              title={t.changePhoto}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: BRAND_GREEN }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" disabled={uploadingAvatar} />
            </label>
            {avatarPreview && (
              <button
                onClick={() => { setAvatarPreview(null); handleChange('avatar', null); }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
                title={t.removePhoto}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold truncate" style={{ color: BRAND_GREEN }}>
              {formData.fullName || 'Seu Nome'}
            </h1>
            <p className="text-gray-500 text-sm truncate">{formData.email}</p>
            {uploadingAvatar && <p className="text-xs text-blue-500 mt-1">⏳ Enviando foto...</p>}
            {errors.avatar && <p className="text-xs text-red-500 mt-1">{errors.avatar}</p>}
          </div>
        </div>
      </Card>

      {/* Informações Pessoais */}
      <Card>
        <h3 className="text-base font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.personalInfo}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.fullName} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.fullName || ''}
              onChange={(e) => handleChange('fullName', e.target.value)}
              placeholder="Ex: João da Silva"
              className={inputCls(errors.fullName)}
              style={inputStyle(errors.fullName)}
            />
            {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.email} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Ex: joao@email.com"
              className={inputCls(errors.email)}
              style={inputStyle(errors.email)}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.phone}
            </label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="Ex: (11) 91234-5678"
              className={inputCls(false)}
              style={inputStyle(false)}
            />
          </div>

          {/* Data de Nascimento */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.birthDate}
            </label>
            <input
              type="date"
              value={formData.dateOfBirth || ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              className={inputCls(false)}
              style={inputStyle(false)}
            />
          </div>

          {/* Gênero */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.gender}
            </label>
            <select
              value={formData.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              className={inputCls(false)}
              style={inputStyle(false)}
            >
              <option value="">{t.selectGender}</option>
              <option value="masculino">{t.genderM}</option>
              <option value="feminino">{t.genderF}</option>
              <option value="outro">{t.genderO}</option>
              <option value="prefiro-nao-dizer">{t.genderNA}</option>
            </select>
          </div>

          {/* Nível de Prática */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.practiceLevel}
            </label>
            <select
              value={formData.practiceLevel || ''}
              onChange={(e) => handleChange('practiceLevel', e.target.value)}
              className={inputCls(false)}
              style={inputStyle(false)}
            >
              <option value="">{t.selectLevel}</option>
              <option value="amador">{t.levelAmateur}</option>
              <option value="semiprofissional">{t.levelSemiPro}</option>
              <option value="profissional">{t.levelProfessional}</option>
              <option value="comissao">{t.levelCoach}</option>
              <option value="outro">{t.levelOther}</option>
            </select>
          </div>

        </div>
      </Card>

      {/* Localização */}
      <Card>
        <h3 className="text-base font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.locationSection}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.state}
            </label>
            <select
              value={formData.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
              className={inputCls(false)}
              style={inputStyle(false)}
            >
              <option value="">{t.selectState}</option>
              {[['AC','Acre'],['AL','Alagoas'],['AP','Amapá'],['AM','Amazonas'],['BA','Bahia'],
                ['CE','Ceará'],['DF','Distrito Federal'],['ES','Espírito Santo'],['GO','Goiás'],
                ['MA','Maranhão'],['MT','Mato Grosso'],['MS','Mato Grosso do Sul'],['MG','Minas Gerais'],
                ['PA','Pará'],['PB','Paraíba'],['PR','Paraná'],['PE','Pernambuco'],['PI','Piauí'],
                ['RJ','Rio de Janeiro'],['RN','Rio Grande do Norte'],['RS','Rio Grande do Sul'],
                ['RO','Rondônia'],['RR','Roraima'],['SC','Santa Catarina'],['SP','São Paulo'],
                ['SE','Sergipe'],['TO','Tocantins']
              ].map(([uf, name]) => (
                <option key={uf} value={uf}>{name}</option>
              ))}
            </select>
          </div>

          {/* Cidade */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: BRAND_GREEN }}>
              {t.city}
            </label>
            <input
              type="text"
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Ex: São Paulo"
              className={inputCls(false)}
              style={inputStyle(false)}
            />
          </div>

        </div>
      </Card>

      {/* Botão Inserir / Salvar */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-sm disabled:opacity-60"
          style={{ backgroundColor: BRAND_GREEN }}
        >
          {isSaving ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              {t.saving}
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isProfileIncomplete ? t.insertData : t.saveChanges}
            </>
          )}
        </button>

        {saved && (
          <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: BRAND_GREEN }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t.profileComplete}!
          </span>
        )}
      </div>
    </div>
  );
};

// --- Seção 2: Notificações ---
const NotificationsSection = ({ notificationSettings, onUpdate }) => {
  const [settings, setSettings] = useState(notificationSettings);

  useEffect(() => {
    setSettings(notificationSettings);
  }, [notificationSettings]);

  const handleToggle = (category, setting) => {
    const newSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: !settings[category][setting]
      }
    };
    setSettings(newSettings);
    onUpdate(newSettings);
  };

  const handleChannelToggle = (category, setting, channel) => {
    const newSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: {
          ...settings[category][setting],
          [channel]: !settings[category][setting][channel]
        }
      }
    };
    setSettings(newSettings);
    onUpdate(newSettings);
  };

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          Alertas de Partidas
        </h3>
        <div className="space-y-4">
          {Object.entries(settings.matchAlerts || {}).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">
                  {key === 'matchStart' && 'Início da partida'}
                  {key === 'goalsResults' && 'Gols e resultados'}
                  {key === 'cardsEvents' && 'Cartões e eventos importantes'}
                </p>
                <p className="text-sm text-gray-500">
                  {key === 'matchStart' && 'Receba notificações quando suas partidas favoritas começarem'}
                  {key === 'goalsResults' && 'Alertas para gols, resultados e estatísticas'}
                  {key === 'cardsEvents' && 'Notificações sobre cartões e eventos marcantes'}
                </p>
              </div>
              <ToggleSwitch
                checked={value}
                onChange={() => handleToggle('matchAlerts', key)}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          Notícias e Atualizações
        </h3>
        <div className="space-y-4">
          {Object.entries(settings.newsUpdates || {}).map(([key, value]) => (
            <div key={key} className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {key === 'weeklyNewsletter' && 'Newsletter semanal'}
                    {key === 'favoriteTeams' && 'Notícias sobre meus times favoritos'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {key === 'weeklyNewsletter' && 'Resumo semanal com as principais notícias esportivas'}
                    {key === 'favoriteTeams' && 'Atualizações específicas dos times que você acompanha'}
                  </p>
                </div>
                <ToggleSwitch
                  checked={value.enabled}
                  onChange={() => handleToggle('newsUpdates', key)}
                />
              </div>
              
              {value.enabled && (
                <div className="ml-6 space-y-2">
                  <p className="text-sm text-gray-600">Como deseja receber:</p>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={value.email || false}
                        onChange={() => handleChannelToggle('newsUpdates', key, 'email')}
                        className="mr-2"
                        style={{ accentColor: BRAND_GREEN }}
                      />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={value.push || false}
                        onChange={() => handleChannelToggle('newsUpdates', key, 'push')}
                        className="mr-2"
                        style={{ accentColor: BRAND_GREEN }}
                      />
                      <span className="text-sm">Notificação Push</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// --- Seção 3: Segurança ---
const SecuritySection = ({ onPasswordChange }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Senha atual é obrigatória';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'Nova senha é obrigatória';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Nova senha deve ter pelo menos 8 caracteres';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    
    setIsSubmitting(true);
    try {
      await onPasswordChange(passwordData);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      alert('Senha alterada com sucesso!');
    } catch (error) {
      alert('Erro ao alterar senha. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.changePassword}
        </h3>
        
        {!showPasswordForm ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{t.accountPassword}</p>
              <p className="text-sm text-gray-500">Última alteração há 30 dias</p>
            </div>
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 text-sm font-semibold rounded-lg border transition-colors"
              style={{ 
                borderColor: BRAND_GREEN, 
                color: BRAND_GREEN,
                backgroundColor: 'white'
              }}
            >
              Alterar Senha
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: BRAND_GREEN }}>
                Senha Atual
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handleChange('currentPassword', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  borderColor: errors.currentPassword ? '#ef4444' : BRAND_GREEN,
                  focusRingColor: BRAND_GREEN
                }}
                required
              />
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: BRAND_GREEN }}>
                Nova Senha
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  borderColor: errors.newPassword ? '#ef4444' : BRAND_GREEN,
                  focusRingColor: BRAND_GREEN
                }}
                required
              />
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: BRAND_GREEN }}>
                Confirmar Nova Senha
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  borderColor: errors.confirmPassword ? '#ef4444' : BRAND_GREEN,
                  focusRingColor: BRAND_GREEN
                }}
                required
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: BRAND_GREEN }}
              >
                {isSubmitting ? t.savingPw : t.savePassword}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.cancel}
              </button>
            </div>
          </form>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.activeSessions}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm">💻</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Chrome, Windows</p>
                <p className="text-sm text-gray-500">Sessão atual • São Paulo, SP</p>
              </div>
            </div>
            <span className="text-xs text-green-600 font-medium">Ativa</span>
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-gray-600 text-sm">📱</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Safari, iPhone</p>
                <p className="text-sm text-gray-500">Última atividade há 2 horas</p>
              </div>
            </div>
            <button className="text-xs text-red-600 font-medium hover:underline">
              Desconectar
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// --- Seção 4: Dados e Privacidade ---
const PrivacySection = ({ onDataExport, onAccountDelete }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await onDataExport();
      alert('Seus dados foram preparados para download. Você receberá um email quando estiver pronto.');
    } catch (error) {
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'EXCLUIR') return;
    
    try {
      await onAccountDelete();
      alert('Sua conta foi excluída com sucesso.');
    } catch (error) {
      alert('Erro ao excluir conta. Tente novamente.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Exportar Dados */}
      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.exportData}
        </h3>
        <div className="space-y-4">
          <p className="text-gray-700">
            Você tem o direito de acessar e receber uma cópia dos seus dados pessoais 
            em um formato portável. Isso inclui suas informações de perfil, preferências 
            e histórico de atividades.
          </p>
          <button
            onClick={handleExportData}
            disabled={isExporting}
            className="px-4 py-2 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            {isExporting ? 'Preparando...' : 'Fazer Download dos Meus Dados'}
          </button>
        </div>
      </Card>

      {/* Gerenciar Conta */}
      <Card>
        <h3 className="text-lg font-semibold mb-4" style={{ color: BRAND_GREEN }}>
          {t.manageAccount}
        </h3>
        <div className="space-y-4">
          <div className="p-4 border border-red-200 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
            <h4 className="font-medium text-red-800 mb-2">{t.dangerZone}</h4>
            <p className="text-sm text-red-700 mb-3">
              A exclusão da conta é permanente e não pode ser desfeita. 
              Todos os seus dados, preferências e favoritos serão permanentemente removidos.
            </p>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 text-red-600 font-semibold border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
            >
              {t.deleteAccount}
            </button>
          </div>
        </div>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-800">Você tem certeza?</h3>
                  <p className="text-sm text-red-600">Esta ação não pode ser desfeita</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Ao excluir sua conta, você perderá permanentemente:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Todas as suas informações de perfil</li>
                  <li>Seus times e ligas favoritos</li>
                  <li>Histórico de notificações e preferências</li>
                  <li>Acesso a todos os recursos da plataforma</li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Para confirmar, digite <strong>EXCLUIR</strong> no campo abaixo:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Digite EXCLUIR"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmation !== 'EXCLUIR'}
                  className="flex-1 px-4 py-2 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Excluir Conta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Componente Principal ---
const UserProfilePage = () => {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState('profile');
  const [userData, setUserData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.birth_date || '',
    gender: user?.gender || '',
    state: user?.state || '',
    city: user?.city || '',
    favoriteSports: user?.favorite_sports || [],
    practiceLevel: user?.practice_level || '',
    avatar: user?.avatar || null,
    createdAt: user?.loginTime || user?.created_at || new Date().toISOString()
  });
  
  // Atualiza userData quando user mudar
  React.useEffect(() => {
    if (user) {
      setUserData({
        fullName: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: user.birth_date || '',
        gender: user.gender || '',
        state: user.state || '',
        city: user.city || '',
        favoriteSports: user.favorite_sports || [],
        practiceLevel: user.practice_level || '',
        avatar: user.avatar || null,
        createdAt: user.loginTime || user.created_at || new Date().toISOString()
      });
    }
  }, [user]);

  const [notificationSettings, setNotificationSettings] = useState({
    matchAlerts: {
      matchStart: true,
      goalsResults: true,
      cardsEvents: false
    },
    newsUpdates: {
      weeklyNewsletter: {
        enabled: true,
        email: true,
        push: false
      },
      favoriteTeams: {
        enabled: true,
        email: true,
        push: true
      }
    }
  });

  const handleProfileUpdate = async (newData) => {
    console.log('[UserProfilePage] 📤 Atualizando perfil:', newData);
    
    try {
      // Mapear avatar para avatarUrl para o backend
      const dataToSend = { ...newData };
      if ('avatar' in dataToSend) {
        dataToSend.avatarUrl = dataToSend.avatar;
        delete dataToSend.avatar;
      }
      
      const response = await AZURE.put('/auth/profile', dataToSend);
      
      if (response.data.success) {
        console.log('[UserProfilePage] ✅ Perfil atualizado no backend');
        setUserData(prev => ({ ...prev, ...newData }));
        
        // Atualiza o contexto se disponível
        if (updateProfile) {
          updateProfile({
            name: newData.fullName,
            email: newData.email,
            avatar: newData.avatar,
            ...response.data.data
          });
        }
        alert('Perfil atualizado com sucesso!');
      }
    } catch (error) {
      console.error('[UserProfilePage] ❌ Erro ao atualizar perfil:', error);
      alert('Erro ao atualizar perfil: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleNotificationUpdate = async (newSettings) => {
    console.log('[UserProfilePage] 📤 Atualizando notificações:', newSettings);
    
    try {
      const response = await AZURE.put('/auth/notifications', newSettings);
      
      if (response.data.success) {
        console.log('[UserProfilePage] ✅ Notificações atualizadas');
        setNotificationSettings(newSettings);
        alert('Preferências de notificações atualizadas!');
      }
    } catch (error) {
      console.error('[UserProfilePage] ❌ Erro ao atualizar notificações:', error);
      alert('Erro ao atualizar notificações: ' + (error.response?.data?.error || error.message));
    }
  };

  const handlePasswordChange = async (passwordData) => {
    console.log('[UserProfilePage] 📤 Alterando senha');
    
    try {
      const response = await AZURE.put('/auth/password', passwordData);
      
      if (response.data.success) {
        console.log('[UserProfilePage] ✅ Senha alterada');
        alert('Senha alterada com sucesso!');
      }
    } catch (error) {
      console.error('[UserProfilePage] ❌ Erro ao alterar senha:', error);
      alert('Erro ao alterar senha: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDataExport = async () => {
    console.log('[UserProfilePage] 📤 Exportando dados');
    
    try {
      const response = await AZURE.get('/auth/export', { responseType: 'blob' });
      
      // Cria download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arenastats_dados_${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      console.log('[UserProfilePage] ✅ Dados exportados');
      alert('Seus dados foram exportados com sucesso!');
    } catch (error) {
      console.error('[UserProfilePage] ❌ Erro ao exportar dados:', error);
      alert('Erro ao exportar dados: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAccountDelete = async () => {
    console.log('[UserProfilePage] 📤 Tentando excluir conta');
    
    const confirmDelete = window.confirm(
      'Tem certeza que deseja excluir sua conta? Esta ação não pode ser desfeita e todos os seus dados serão permanentemente removidos.'
    );
    
    if (!confirmDelete) {
      console.log('[UserProfilePage] ⚠️ Exclusão cancelada pelo usuário');
      return;
    }
    
    try {
      const response = await AZURE.delete('/auth/delete');
      
      if (response.data.success) {
        console.log('[UserProfilePage] ✅ Conta excluída com sucesso');
        alert('Sua conta foi excluída com sucesso.');
        
        // Faz logout e redireciona para home
        logout();
        navigate('/');
      }
    } catch (error) {
      console.error('[UserProfilePage] ❌ Erro ao excluir conta:', error);
      alert('Erro ao excluir conta: ' + (error.response?.data?.error || error.message));
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection userData={userData} onUpdate={handleProfileUpdate} />;
      case 'notifications':
        return <NotificationsSection notificationSettings={notificationSettings} onUpdate={handleNotificationUpdate} />;
      case 'security':
        return <SecuritySection onPasswordChange={handlePasswordChange} />;
      case 'privacy':
        return <PrivacySection onDataExport={handleDataExport} onAccountDelete={handleAccountDelete} />;
      default:
        return <ProfileSection userData={userData} onUpdate={handleProfileUpdate} />;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
      <div className="container mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <ProfileSidebar activeSection={activeSection} onSectionChange={setActiveSection} />
          
          {/* Content Area */}
          <div className="flex-1">
            {renderActiveSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
